/**
 * ContextPanel — where this conversation's context window actually went.
 *
 * Reads the CLI's own measurement (`get_context_usage`, the same data
 * `/context` shows), captured server-side at the end of each turn and stored on
 * the conversation. Until now the occupancy bar was a single number with no way
 * to ask *why* it was that big; this answers that — and makes the per-MCP-tool
 * cost visible, which is the evidence base for trimming the tool manifest.
 *
 * The denominator is deliberately **not** the CLI's `max_tokens`: the CLI sizes
 * that from its own model registry, which needn't know an OpenRouter-routed
 * model's real window. We pair the measured total with CoWork's own limit table
 * (`getContextLimit`) and say plainly which model the reading is for.
 */

import { useEffect, useMemo, useState } from "react";

import { getContextLimit } from "@/api/modelLimits";
import { useChatStore } from "@/stores/chatStore";
import { useModelCatalogStore } from "@/stores/modelCatalogStore";
import { formatTokens } from "@/components/chat/breakdownShared";

/** Category → bar colour. Falls back to the accent for anything new the CLI
 *  starts reporting, so an unknown category still renders. */
const CATEGORY_COLOR: Record<string, string> = {
  "System prompt": "bg-jove",
  "System tools": "bg-oxy",
  Tools: "bg-oxy",
  "MCP tools": "bg-gold",
  Messages: "bg-jove-80",
  "Memory files": "bg-hydra",
  Agents: "bg-jove-60",
};

function colorFor(name: string): string {
  return CATEGORY_COLOR[name] ?? "bg-jove-60";
}

function Bar({ pct, className }: { pct: number; className: string }) {
  return (
    <div className="h-1.5 rounded-full bg-space-20 overflow-hidden">
      <div
        className={`h-full rounded-full ${className} transition-[width] duration-500`}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

export function ContextPanel() {
  const breakdown = useChatStore((s) => s.contextBreakdown);
  const selectedModel = useChatStore((s) => s.selectedModel);
  const effectiveModel = useChatStore((s) => s.effectiveModel);
  const [showTools, setShowTools] = useState(false);

  // Subscribing to the catalog (rather than only reading it inside
  // `getContextLimit`) is what makes an OpenRouter denominator correct: the
  // fetch usually resolves after first paint, and without a subscription the
  // memo would stay pinned to the 200K fallback captured on mount.
  const contextWindows = useModelCatalogStore((s) => s.contextWindows);
  const loadCatalog = useModelCatalogStore((s) => s.load);
  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  const model = breakdown?.model || effectiveModel || selectedModel;
  const limit = useMemo(() => getContextLimit(model), [model, contextWindows]);

  if (!breakdown) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-xs text-space-50">No context reading yet.</p>
        <p className="text-[11px] text-space-40 mt-1.5 max-w-[260px] mx-auto">
          This fills in when the next turn finishes — it's measured by the agent
          runtime at the end of each turn.
        </p>
      </div>
    );
  }

  const total = breakdown.total_tokens;
  const pctOfLimit = limit > 0 ? (total / limit) * 100 : 0;
  // Deferred schemas aren't loaded, so they're not in `total_tokens`. Split
  // them out rather than letting them pad bars that are meant to add up.
  const loaded = breakdown.categories.filter((c) => !c.deferred);
  const deferred = breakdown.categories.filter((c) => c.deferred);
  const largest = Math.max(1, ...loaded.map((c) => c.tokens));
  const deferredTotal = deferred.reduce((sum, c) => sum + c.tokens, 0);
  const toolEntries = Object.entries(breakdown.mcp_tools)
    .sort((a, b) => b[1] - a[1]);
  const toolTotal = toolEntries.reduce((sum, [, v]) => sum + v, 0);

  return (
    <div className="px-4 py-3 space-y-4">
      {/* Occupancy */}
      <div>
        <div className="flex items-baseline justify-between gap-2 mb-1.5">
          <span className="text-sm font-semibold text-space tabular-nums">
            {formatTokens(total)}
            <span className="text-space-40 font-normal"> / {formatTokens(limit)}</span>
          </span>
          <span className="text-xs text-space-50 tabular-nums">
            {pctOfLimit.toFixed(0)}%
          </span>
        </div>
        <Bar pct={pctOfLimit} className={pctOfLimit > 85 ? "bg-hydra" : "bg-jove"} />
        {model ? (
          <p className="text-[10px] text-space-40 mt-1.5 truncate" title={model}>
            measured for {model}
          </p>
        ) : null}
      </div>

      {/* Where it went */}
      {loaded.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-[10px] uppercase tracking-wider text-space-40 font-semibold">
            Breakdown
          </h4>
          {loaded.map((c) => (
            <div key={c.name}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-xs text-space-50 truncate">{c.name}</span>
                <span className="text-[11px] text-space-40 tabular-nums shrink-0">
                  {formatTokens(c.tokens)}
                </span>
              </div>
              {/* Scaled to the LARGEST category, not the window — the point is
                  comparing slices to each other, and against a big window every
                  bar would otherwise be a sliver. */}
              <Bar pct={(c.tokens / largest) * 100} className={colorFor(c.name)} />
            </div>
          ))}
        </div>
      )}

      {/* Not loaded yet — real weight, but not part of the total above, so it
          gets its own line rather than a bar that wouldn't add up. */}
      {deferred.length > 0 && (
        <p className="text-[11px] text-space-40 leading-relaxed">
          <span className="font-medium text-space-50">
            +{formatTokens(deferredTotal)}
          </span>{" "}
          of tool schemas are deferred — they load only if the agent reaches for
          them, so they aren't counted above.
        </p>
      )}

      {/* Per-MCP-tool cost — what the tool manifest charges every turn */}
      {toolEntries.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowTools((v) => !v)}
            className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider
                       text-space-40 font-semibold hover:text-space-50 transition-colors"
          >
            <svg
              className={`w-3 h-3 transition-transform ${showTools ? "rotate-90" : ""}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            MCP tools
            <span className="ml-auto normal-case tracking-normal tabular-nums">
              {toolEntries.length} · {formatTokens(toolTotal)}
            </span>
          </button>
          {showTools && (
            <ul className="mt-2 space-y-1">
              {toolEntries.map(([name, tokens]) => (
                <li key={name} className="flex items-baseline justify-between gap-2">
                  <span className="text-[11px] font-mono text-space-50 truncate" title={name}>
                    {name.replace(/^mcp__/, "")}
                  </span>
                  <span className="text-[10px] text-space-40 tabular-nums shrink-0">
                    {formatTokens(tokens)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
